import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, ShieldAlert } from "lucide-react";
import api from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const pwOk = useMemo(() => pw.length >= 12, [pw]);
  const matches = useMemo(() => pw && pw === confirm, [pw, confirm]);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0FAFA] p-6">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="reset-invalid-panel">
          <div className="flex items-center gap-2 text-amber-700 mb-3">
            <ShieldAlert className="h-6 w-6" />
            <h2 className="font-heading text-xl font-bold">Invalid link</h2>
          </div>
          <p className="text-sm text-slate-700">This reset link is missing a token or has been tampered with.</p>
          <Link to="/forgot-password" className="mt-5 inline-block text-sm font-semibold text-[#0D7377] hover:text-[#0A5C5F]">Request a new link →</Link>
        </div>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!pwOk) { setErr("Password must be at least 12 characters."); return; }
    if (!matches) { setErr("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      // Redirect to login after 3 seconds so the user can read the confirmation
      setTimeout(() => navigate("/login", { replace: true }), 3000);
    } catch (e2) {
      setErr(e2.response?.data?.detail || e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F0FAFA] p-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-10 w-10 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden">
            <img src="/logo.png" alt="Rabah Concept" className="h-9 w-9 object-contain" />
          </div>
          <p className="font-heading font-bold text-xl text-slate-900">Rabah Concept</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="reset-password-panel">
          {!done ? (
            <>
              <h2 className="font-heading text-2xl font-bold text-slate-900">Set a new password</h2>
              <p className="mt-1 text-sm text-slate-600">Choose a password with at least 12 characters. Cannot match your last 5 passwords.</p>
              <form onSubmit={submit} className="mt-5 space-y-4">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">New password</label>
                  <input
                    type="password"
                    required
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="At least 12 characters"
                    data-testid="reset-password-input"
                    autoComplete="new-password"
                    className="mt-1 w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-[#0D7377] focus:ring-2 focus:ring-[#0D7377]/20 outline-none bg-white"
                  />
                  <p className={`text-xs mt-1 ${pw && !pwOk ? "text-red-600" : "text-slate-500"}`}>
                    {pw && !pwOk ? "Too short — need 12+ characters" : `${pw.length}/12 characters`}
                  </p>
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Confirm new password</label>
                  <input
                    type="password"
                    required
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Type the password again"
                    data-testid="reset-confirm-input"
                    autoComplete="new-password"
                    className="mt-1 w-full px-3 py-2.5 rounded-md border border-slate-300 focus:border-[#0D7377] focus:ring-2 focus:ring-[#0D7377]/20 outline-none bg-white"
                  />
                  {confirm && !matches && <p className="text-xs mt-1 text-red-600">Passwords do not match</p>}
                </div>
                {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="reset-error">{err}</div>}
                <button type="submit" disabled={busy || !pwOk || !matches} data-testid="reset-submit-button" className="w-full inline-flex items-center justify-center gap-2 bg-[#0D7377] hover:bg-[#0A5C5F] text-white font-semibold py-2.5 rounded-md disabled:opacity-50 min-h-[44px]">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password
                </button>
              </form>
            </>
          ) : (
            <div data-testid="reset-done-panel">
              <div className="flex items-center gap-2 text-emerald-700 mb-3">
                <CheckCircle2 className="h-6 w-6" />
                <h2 className="font-heading text-xl font-bold">Password updated</h2>
              </div>
              <p className="text-sm text-slate-700">Your password has been changed. Redirecting you to sign in…</p>
              <Link to="/login" className="mt-4 inline-block text-sm font-semibold text-[#0D7377] hover:text-[#0A5C5F]">Sign in now →</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
